
import Vue from 'vue';
import {
  EntityAttribute, EntityAttributeOption, EntityType, ExtractedEntity,
  IngestTextFragment, Label, SemanticSearchResult, Suggestion,
} from '@/model';
import LabelPicker from '@/components/LabelPicker.vue';
import LabelChip from '@/components/LabelChip.vue';
import labels, { labelSorter, getUsePotential } from '@/store/labels';
import { distinct, notEmpty } from '@/util/array-util';
import api from '@/rest-api';
import { intersects } from '@/util/text-selection';
import RelatedFragments from '@/components/RelatedFragments.vue';

export default Vue.extend({
  name: 'DocumentFragment',
  components: {
    LabelPicker, LabelChip, RelatedFragments,
  },

  props: {
    value: { type: Object as () => IngestTextFragment, required: true },
    context: { type: Array as () => Label[], default: () => [] },
    edit: { type: Boolean, default: false },
    focusLabelSelector: { type: Boolean, default: false },
    hideText: { type: Boolean, default: false },
    documentId: { type: Number as () => number },
    selectedText: { type: String, default: '' },
    suggestions: { type: Array as () => Suggestion[], default: () => [] },
    priorityTypes: { type: Array as () => string[], default: () => [] },
    extractedEntities: { type: Array as () => ExtractedEntity[], default: () => [] },
    entityTypes: { type: Array as () => EntityType[], default: () => [] },
  },

  data() {
    return {
      editItem: {} as Partial<IngestTextFragment>,
      editLabels: [] as Label[],
      relatedFragments: [] as SemanticSearchResult[],
      showRelatedFragments: false,
    };
  },

  watch: {
    edit: {
      immediate: true,
      handler(enabled) {
        if (enabled) {
          this.editItem = {
            description: this.value.description,
            correction: this.value.correction,
            color: this.value.color,
          };
          this.editLabels = this.value.labels.map((la) => labels.getters.lookup.get(la.labelId)).filter(notEmpty);
          this.relatedFragments = [];
          if (this.selectedText && this.selectedText.length > 0) {
            this.loadRelatedFragments(this.selectedText);
          }
        }
        this.$emit('edit', this.edit);
      },
    },
    selectedText: {
      immediate: true,
      handler(selectedText) {
        this.relatedFragments = [];
        if (this.edit && selectedText && selectedText.length > 0) {
          this.loadRelatedFragments(selectedText);
        }
      },
    },
  },

  computed: {
    usePotential(): boolean {
      return getUsePotential();
    },
    fragmentStyle(): object {
      const c = labels.getters.lookup.get(this.value.labels[0]?.labelId)?.color;
      if (!c) {
        return {};
      }
      const luminance = (c.r * 299 + c.g * 587 + c.b * 114) / 1000;
      return {
        backgroundColor: `rgb(${c.r},${c.g},${c.b})`,
        color: luminance > 128 ? '#000000' : '#ffffff',
      };
    },
    fragmentText(): string {
      // if (this.value.description) {
      //   return this.value.description;
      // }
      return this.value.labels.map((la) => labels.getters.lookup.get(la.labelId)?.code).join(', ') || 'Fragment';
    },
    overlappingSuggestionsLabel(): Label[] {
      return distinct(this.suggestions
        .filter((s) => this.value.boxes.some((b) => b.boxId === s.boxId && intersects(s, b)))
        .map((s) => s.labelId)).map((id) => labels.getters.lookup.get(id)).filter(notEmpty);
    },
    entityLabels(): Label[] {
      const boxIds = new Set(this.value.boxes.map((b) => b.boxId));
      const matching = this.extractedEntities.filter((e) => e.spans.some((s) => s.boxId != null && boxIds.has(s.boxId)));
      if (matching.length === 0) return [];

      const entityTypeMap = new Map(this.entityTypes.map((et) => [et.id, et]));
      const attrMap = new Map<number, EntityAttribute>();
      const optionMap = new Map<number, EntityAttributeOption>();
      for (const et of this.entityTypes) {
        for (const attr of et.attributes) {
          attrMap.set(attr.id, attr);
          for (const opt of (attr.options || [])) {
            optionMap.set(opt.id, opt);
          }
        }
      }

      const labelIds = new Set<number>();
      for (const entity of matching) {
        const et = entityTypeMap.get(entity.entityTypeId);
        if (et?.labelId) labelIds.add(et.labelId);
        for (const attr of (entity.attributes || [])) {
          const attrDef = attrMap.get(attr.entityAttributeId);
          if (attrDef?.labelId) labelIds.add(attrDef.labelId);
          if (attr.entityAttributeOptionId != null) {
            const opt = optionMap.get(attr.entityAttributeOptionId);
            if (opt?.labelId) labelIds.add(opt.labelId);
          }
        }
      }

      return [...labelIds].map((id) => labels.getters.lookup.get(id)).filter(notEmpty);
    },
  },

  mounted() {
    document.addEventListener('keydown', this.handleKeydown);
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.handleKeydown);
  },

  methods: {
    save() {
      const frag: IngestTextFragment = {
        ...this.value,
        ...this.editItem,
        labels: this.editLabels.map((label) => {
          const assignment = this.value.labels.find((la) => la.labelId === label.id);
          return assignment || { labelId: label.id };
        }),
      };
      this.$emit('input', frag);
      this.collapse();
    },
    deleteFragment() {
      this.$emit('deleteFragment', this.value.id);
      this.collapse();
    },
    revertFragment() {
      this.$emit('revertFragment', this.value.id);
      this.collapse();
    },
    collapse() {
      this.$emit('collapse');
    },
    handleKeydown(e: KeyboardEvent) {
      // ctrl s to save
      if (e.ctrlKey && e.key === 's' && this.edit) {
        this.save();
        e.preventDefault();
      }
    },
    async loadRelatedFragments(selectedText: string) {
      try {
        this.relatedFragments = (await api.semanticSearchExpand({
          query: selectedText,
          limit: 10,
          filters: [{
            field: 'documentId',
            values: [this.documentId],
            negate: true,
          }],
        }));
      } catch (error) {
        // Related fragments are a best-effort suggestion; degrade silently if search is unavailable.
        // eslint-disable-next-line no-console
        console.error('Failed to load related fragments:', error);
      }
    },
    fragmnetLabels(fragments: IngestTextFragment[]): Label[] {
      const allLabels = distinct(fragments.flatMap((f) => f.labels).map((l) => l.labelId));
      return allLabels
        .map((id) => labels.getters.lookup.get(id))
        .filter(notEmpty)
        .sort(labelSorter(['type', 'length', 'alpha']));
    },
    relatedLabels(related: SemanticSearchResult[], suggestionsLabels: Label[], selectedLabels: Label[]): Label[] {
      // frequency of labels
      const counter = new Map<number, number>();
      const scoreCounter = new Map<number, number>();
      related.forEach((result) => {
        const ls = this.fragmnetLabels(result.fragments);
        ls.forEach((label) => {
          const count = counter.get(label.id) ?? 0;
          counter.set(label.id, count + 1);
          const current = scoreCounter.get(label.id) ?? 0;
          const score = 1 - (result.item.score ?? 0);
          scoreCounter.set(label.id, current + score);
        });
      });

      const maxScore = related.map((r) => 1 - r.item.score).reduce((a, b) => a + b, 0);
      // use max score to normalize
      const normalizedScoreCounter = new Map<number, number>();
      scoreCounter.forEach((score, id) => {
        normalizedScoreCounter.set(id, score / maxScore);
      });

      const ls = [...normalizedScoreCounter.entries()]
        .filter(([_, count]) => count >= 0.4)
        .map(([id, _]) => labels.getters.lookup.get(id))
        .filter(notEmpty)
        .filter((l) => l.type !== 'Workflow')
        .filter((l) => l.type !== 'Geography');
      return [...ls, ...suggestionsLabels, ...this.entityLabels]
        .sort(labelSorter(['type', 'length', 'alpha']))
        .filter((l) => !selectedLabels.some((sl) => sl.id === l.id))
        .filter((l) => !this.context.some((cl) => cl.id === l.id));
    },
  },

});
